import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import EditActionButton from '../../../../components/designe/ActionButtons/EditActionButton';
import AppUserAvatar from '../../../../components/designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import WarningNoticeBanner from '../../../../components/designe/warning-notice-banner/WarningNoticeBanner';
import AppNavigate from '../../../../components/reusable/app-navigate/AppNavigate';
import ConfirmDialog from '../../../../components/reusable/confirm-dialog/ConfirmDialog';
import UserMultiSelectDialog from '../../../../components/reusable/user-multi-select-dialog/UserMultiSelectDialog';
import { AvatarSize, AvatarTypes } from '../../../../shared/constants';
import { IProgramItem } from '../../../../types/interfaces/program-item';
import { getUserViewName } from '../../../../utils/common/shared-util';
import { useStyles as useAccessStyles } from '../../../users/settings/access-settings/AccessSettings.styles';
import { useProgramShareAccess } from './useProgramShareAccess';

export interface IProgramShareAccessTabProps {
  programItem: IProgramItem | undefined;
  programItemId: string;
  isFetching: boolean;
  redirectTo: string;
}

export default function ProgramShareAccessTab(
  props: IProgramShareAccessTabProps,
) {
  const { programItem, programItemId, isFetching, redirectTo } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useAccessStyles(theme);

  const {
    displayUsers,
    modalUsers,
    selectedUserIds,
    dialogOpen,
    setDialogOpen,
    removeTarget,
    setRemoveTarget,
    handleModalSave,
    handleConfirmRemove,
    isPosting,
    isAccessLoading,
    isAccessFetching,
  } = useProgramShareAccess(programItem, programItemId);
  const showEmptyStateWarning =
    !isAccessLoading && !isAccessFetching && modalUsers.length === 0;

  if (
    !isFetching &&
    programItem &&
    !programItem.permissions?.canConfigureAccess
  ) {
    return <AppNavigate to={redirectTo} replace />;
  }

  if (isFetching || !programItem) {
    return (
      <Box className={classes.loadingBox}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <div className={classes.wrapper}>
        <Box>
          <Box className={classes.sectionHeader}>
            <Typography
              variant="label-text-md"
              className={classes.sectionLabel}>
              {t('trainings.finishedAccessLabel')}
            </Typography>
            <EditActionButton onClick={() => setDialogOpen(true)} />
          </Box>
          <Box className={classes.chips}>
            {displayUsers.length === 0 ? (
              <Typography variant="label-text-md" color="text.secondary">
                {t('common.noDataToDisplayMessage')}
              </Typography>
            ) : (
              displayUsers.map((u) => (
                <Chip
                  key={u.id}
                  className={classes.chip}
                  avatar={
                    <AppUserAvatar
                      entity={u}
                      size={AvatarSize.xs}
                      type={AvatarTypes.round}
                    />
                  }
                  label={getUserViewName(u)}
                  onDelete={() => setRemoveTarget(u)}
                />
              ))
            )}
          </Box>
        </Box>
      </div>

      <UserMultiSelectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t('trainings.finishedAccessModalTitle')}
        subtitle={t('settings.accessModalSubtitle')}
        users={modalUsers}
        selectedUserIds={selectedUserIds}
        onSave={(ids) => void handleModalSave(ids)}
        isSaving={isPosting}
        emptyStateNotice={
          showEmptyStateWarning ? (
            <WarningNoticeBanner title={t('programs.shareEmptyBannerTitle')}>
              {t('programs.shareEmptyBannerDescription')}
            </WarningNoticeBanner>
          ) : null
        }
      />

      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title={t('trainings.finishedAccessRevokeTitle')}
        onSubmit={() => void handleConfirmRemove()}
        isLoading={isPosting && removeTarget !== null}>
        {removeTarget
          ? t('trainings.finishedAccessRevokeMessage', {
              name: getUserViewName(removeTarget),
            })
          : null}
      </ConfirmDialog>
    </>
  );
}
